import { useCallback, useEffect, useState } from 'react';

import {
  approveIdea,
  CONTENT_STATUSES,
  type ContentStatus,
  contentPath,
  declineIdea,
  deleteIdeaBatch,
  fetchIdeaBatch,
  type Idea,
  type IdeaBatch as IdeaBatchDetail,
  type IdeaBatchCounts,
  ideaBatchPath,
  reopenIdea,
  retryIdeaBatch,
} from '../api.ts';
import { FUNNEL_LABEL } from '../BriefDetailsFields.tsx';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { DASHBOARD_POLL_MS, describeError, redirectIfUnauthorised } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { IdeaSection, isSection, seedLine } from '../IdeaSection.tsx';
import { ideaBatchIdFromPath, Link, navigate, useLocation } from '../router.tsx';
import { hourMinute } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import { Badge, IdeaStatusBadge, Notice, PageHeader, Skeleton, StatusBadge } from '../ui.tsx';
import * as wording from '../wording.ts';

/**
 * One idea batch and every idea in it (US-123): where the batch stands, and a
 * row per idea with its draft and the decision waiting on it.
 *
 * Decisions land on their row as soon as the server answers, and the page then
 * re-reads quietly so the stage line and the counts follow. While the batch is
 * waiting or being worked it re-reads on the dashboard's beat.
 */
export function IdeaBatch() {
  const { pathname } = useLocation();
  const batchId = ideaBatchIdFromPath(pathname) ?? '';
  const toast = useToast();

  const [batch, setBatch] = useState<IdeaBatchDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [deleting, setDeleting] = useState<'asking' | 'busy' | null>(null);

  const moving = batch !== null && (batch.status === 'queued' || batch.status === 'working');
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const load = (): void => {
      fetchIdeaBatch(batchId, controller.signal)
        .then((loaded) => {
          setBatch(loaded);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted) return;
          if (redirectIfUnauthorised(cause)) return;
          setError(describeError(cause));
        });
    };
    load();
    const timer = moving
      ? window.setInterval(() => {
          if (document.visibilityState === 'visible') load();
        }, DASHBOARD_POLL_MS)
      : null;
    return () => {
      controller.abort();
      if (timer !== null) window.clearInterval(timer);
    };
  }, [batchId, moving, reloads]);

  /** Puts the server's answer for one idea on its row, then re-reads the rest. */
  const replaceIdea = useCallback(
    (idea: Idea) => {
      setBatch((current) => {
        if (current === null) return current;
        const ideas = current.ideas.map((each) => (each.id === idea.id ? idea : each));
        return { ...current, ideas, counts: countsOf(ideas) };
      });
      reload();
    },
    [reload],
  );

  const retry = (): void => {
    setRetrying(true);
    retryIdeaBatch(batchId)
      .then(() => {
        toast.ok(wording.BATCH_RETRIED);
        reload();
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(`${wording.RETRY_FAILED} ${describeError(cause)}`);
      })
      .finally(() => setRetrying(false));
  };

  const remove = (): void => {
    setDeleting('busy');
    deleteIdeaBatch(batchId)
      .then(() => {
        toast.ok(wording.BATCH_DELETED);
        navigate('/ideas');
      })
      .catch((cause: unknown) => {
        setDeleting(null);
        if (redirectIfUnauthorised(cause)) return;
        toast.error(`${wording.DELETE_FAILED} ${describeError(cause)}`);
        reload();
      });
  };

  const back = { href: '/ideas', label: wording.BATCH_BACK };

  if (batch === null) {
    return (
      <div className="page">
        <PageHeader title={wording.IDEAS} back={back} />
        {error !== null ? (
          <Notice kind="error">
            {wording.BATCH_READ_FAILED} {error}
          </Notice>
        ) : (
          <div className="panel">
            <div className="panel__body">
              <Skeleton lines={5} />
            </div>
          </div>
        )}
      </div>
    );
  }

  const working = batch.status === 'working';
  const primary = batch.repositories.find((link) => link.role === 'primary') ?? null;

  const actions = (
    <>
      {batch.counts.drafted > 0 && (
        <Link className="button button--primary" href={`${ideaBatchPath(batch.id)}/review`}>
          {wording.REVIEW_ONE_BY_ONE}
          <Icon name="chevron-right" />
        </Link>
      )}
      <button
        type="button"
        className="button button--danger"
        disabled={working || deleting !== null}
        title={working ? wording.DELETE_BATCH_WHILE_WORKING : undefined}
        aria-describedby={working ? 'batch-delete-hint' : undefined}
        onClick={() => setDeleting('asking')}
      >
        <Icon name="trash" />
        {wording.DELETE_BATCH}
      </button>
    </>
  );

  return (
    <div className="page">
      <PageHeader
        title={batch.title}
        eyebrow={primary?.name ?? batch.primaryRepository?.name ?? wording.NO_REPOSITORY}
        back={back}
        actions={actions}
      />

      {working && (
        <p className="field__hint batch__delete-hint" id="batch-delete-hint">
          {wording.DELETE_BATCH_WHILE_WORKING}
        </p>
      )}

      {error !== null && (
        <Notice kind="error">
          {wording.BATCH_READ_FAILED} {error}
        </Notice>
      )}

      <div className="panel">
        <div className="panel__body batch__summary">
          <StageLine batch={batch} retrying={retrying} onRetry={retry} />
          <dl className="facts">
            <div className="facts__row">
              <dt>{wording.BATCH_PRIMARY}</dt>
              <dd>{primary?.name ?? batch.primaryRepository?.name ?? wording.NO_REPOSITORY}</dd>
            </div>
            <div className="facts__row">
              <dt>{wording.BATCH_THEME}</dt>
              <dd>
                {batch.theme ?? (
                  <span className="muted">
                    {batch.fromDocument ? wording.BATCH_DOCUMENT : wording.BATCH_NO_THEME}
                  </span>
                )}
              </dd>
            </div>
            <div className="facts__row">
              <dt>{wording.BATCH_IDEAS}</dt>
              <dd>{wording.ideaCount(batch.counts.total)}</dd>
            </div>
            <div className="facts__row">
              <dt>{wording.BATCH_COST}</dt>
              <dd>{wording.dollars(batch.costUsd)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {batch.ideas.length > 0 && (
        <div className="table-wrap table-wrap--cards panel">
          <table className="table table--batch table--cards">
            <thead>
              <tr>
                <th>{wording.IDEA_COLUMN}</th>
                <th>{wording.IDEA_TONE}</th>
                <th>{wording.IDEA_FORMATS}</th>
                <th>{wording.IDEA_STATUS_COLUMN}</th>
                <th className="num">{wording.IDEA_COST}</th>
                <th className="table__actions">
                  <span className="visually-hidden">{wording.IDEA_ACTIONS}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {batch.ideas.map((idea) => (
                <IdeaRow key={idea.id} batchId={batch.id} idea={idea} onChange={replaceIdea} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={wording.DELETE_BATCH_TITLE}
        confirmLabel={wording.DELETE_BATCH_YES}
        cancelLabel={wording.CANCEL}
        busyLabel={wording.DELETING}
        busy={deleting === 'busy'}
        danger
        onConfirm={remove}
        onCancel={() => setDeleting(null)}
      >
        <p>{wording.DELETE_BATCH_BODY}</p>
      </ConfirmDialog>
    </div>
  );
}

/** Where the batch stands, in one plain sentence; a failure offers another go. */
function StageLine({
  batch,
  retrying,
  onRetry,
}: {
  readonly batch: IdeaBatchDetail;
  readonly retrying: boolean;
  readonly onRetry: () => void;
}) {
  const retryButton = (
    <button type="button" className="button button--small" disabled={retrying} onClick={onRetry}>
      <Icon name="sync" />
      {retrying ? wording.WORKING : wording.TRY_AGAIN}
    </button>
  );

  if (batch.status === 'failed') {
    return (
      <div className="batch__stage batch__stage--failed" role="alert">
        <Icon name="x-circle" />
        <span>{batch.error ?? wording.BATCH_STATUS.failed}</span>
        {retryButton}
      </div>
    );
  }

  let tone: 'wait' | 'active' | 'done' = 'wait';
  let text: string;
  if (batch.status === 'queued') {
    text = wording.BATCH_STATUS.queued;
  } else if (batch.status === 'working') {
    tone = 'active';
    if (batch.heldUntil !== null) {
      tone = 'wait';
      text = wording.pausedUntil(hourMinute(batch.heldUntil));
    } else if (batch.ideas.length === 0) {
      text = batch.fromDocument ? wording.IDEAS_READING_DOCUMENT : wording.IDEAS_SUGGESTING;
    } else {
      text = wording.workingOnIdea(currentIdea(batch.ideas), batch.ideas.length);
    }
  } else {
    tone = 'done';
    text = wording.batchDone(batch.counts.drafted);
  }

  return (
    <div className={`batch__stage batch__stage--${tone}`} aria-live="polite">
      <Icon name={tone === 'done' ? 'check-circle' : 'clock'} />
      <span>{text}</span>
      {batch.status === 'done' && batch.counts.failed > 0 && (
        <>
          <span className="muted">{wording.ideasNotWorked(batch.counts.failed)}</span>
          {retryButton}
        </>
      )}
    </div>
  );
}

/** The number of the idea being worked on: the working one, else the next in line. */
function currentIdea(ideas: readonly Idea[]): number {
  const index = ideas.findIndex((idea) => idea.status === 'working');
  if (index >= 0) return index + 1;
  const next = ideas.findIndex((idea) => idea.status === 'raw');
  return next >= 0 ? next + 1 : ideas.length;
}

function IdeaRow({
  batchId,
  idea,
  onChange,
}: {
  readonly batchId: string;
  readonly idea: Idea;
  readonly onChange: (idea: Idea) => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  const act = (run: () => Promise<Idea>, failure: string, after?: () => void): void => {
    setBusy(true);
    run()
      .then((updated) => {
        after?.();
        onChange(updated);
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(`${failure} ${describeError(cause)}`);
      })
      .finally(() => setBusy(false));
  };

  const approve = (): void => {
    act(async () => (await approveIdea(batchId, idea.id)).idea, wording.APPROVE_FAILED);
  };
  const decline = (): void => {
    const trimmed = reason.trim();
    act(() => declineIdea(batchId, idea.id, trimmed === '' ? null : trimmed), wording.DECLINE_FAILED, () => {
      setDeclining(false);
      setReason('');
    });
  };
  const undo = (): void => {
    act(() => reopenIdea(batchId, idea.id), wording.UNDO_FAILED);
  };

  const hasTitle = idea.title !== null && idea.title !== '';
  const hasActions =
    idea.status === 'drafted' ||
    idea.status === 'declined' ||
    (idea.status === 'approved' && idea.contentId !== null && idea.content !== null);

  return (
    <tr className={idea.status === 'failed' ? 'table__row--attention' : undefined}>
      <td className="table__cell--lead">
        <div className="cell-stack">
          {hasTitle && (
            <span className="cell-stack__title">
              <span className="batch__idea-title">{idea.title}</span>
              {idea.kind !== null && <Badge tone="neutral">{wording.IDEA_KIND[idea.kind]}</Badge>}
            </span>
          )}
          <span className={hasTitle ? 'cell-stack__meta' : 'batch__idea-title'}>
            <span className="batch__position">{idea.position + 1}.</span> {seedLine(idea)}
          </span>
          {isSection(idea) && <IdeaSection seed={idea.seed} />}
          {idea.briefDetails.funnelStages.length > 0 && (
            <span className="cell-stack__badges" aria-label="Funnel">
              {idea.briefDetails.funnelStages.map((stage) => (
                <Badge key={stage}>{FUNNEL_LABEL[stage]}</Badge>
              ))}
            </span>
          )}
          {idea.status === 'failed' && idea.error !== null && (
            <span className="cell-stack__meta batch__error">{idea.error}</span>
          )}
          {idea.status === 'declined' && (
            <span className="cell-stack__meta">
              {idea.declineReason === null
                ? wording.DECLINED_NO_REASON
                : `${wording.DECLINED_BECAUSE} ${idea.declineReason}`}
            </span>
          )}
        </div>
      </td>
      <td className="table__phrase" data-label={wording.IDEA_TONE}>
        {idea.tone ?? ''}
      </td>
      <td className="table__phrase" data-label={wording.IDEA_FORMATS}>
        {idea.formats === null || idea.formats.length === 0 ? '' : idea.formats.join(', ')}
      </td>
      <td data-label={wording.IDEA_STATUS_COLUMN}>
        <span className="batch__status">
          <IdeaStatusBadge status={idea.status} />
          {idea.status === 'approved' && <ContentState idea={idea} />}
        </span>
      </td>
      <td className="num table__phrase" data-label={wording.IDEA_COST}>
        {wording.dollars(idea.costUsd)}
      </td>
      <td className="table__actions table__cell--foot">
        {hasActions && (
          <span className="batch__actions">
            {idea.status === 'drafted' && (
              <>
                <button type="button" className="button button--small" disabled={busy} onClick={() => setDeclining(true)}>
                  {wording.DECLINE}
                </button>
                <button type="button" className="button button--small button--primary" disabled={busy} onClick={approve}>
                  <Icon name="check" />
                  {busy ? wording.WORKING : wording.APPROVE}
                </button>
              </>
            )}
            {idea.status === 'declined' && (
              <button type="button" className="button button--small button--quiet" disabled={busy} onClick={undo}>
                {busy ? wording.WORKING : wording.UNDO}
              </button>
            )}
            {idea.status === 'approved' && idea.contentId !== null && idea.content !== null && (
              <Link className="button button--small button--quiet" href={contentPath(idea.contentId)}>
                {wording.OPEN_CONTENT}
                <Icon name="chevron-right" />
              </Link>
            )}
          </span>
        )}
        {idea.status === 'drafted' && (
          <ConfirmDialog
            open={declining}
            title={wording.DECLINE_TITLE}
            confirmLabel={wording.DECLINE_YES}
            cancelLabel={wording.CANCEL}
            busy={busy}
            onConfirm={decline}
            onCancel={() => {
              setDeclining(false);
              setReason('');
            }}
          >
            <p>
              <strong>{idea.title ?? seedLine(idea)}</strong>
            </p>
            <label className="field__label" htmlFor={`decline-${idea.id}`}>
              {wording.DECLINE_REASON}
            </label>
            <textarea
              id={`decline-${idea.id}`}
              className="field__input"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <p className="field__hint">{wording.DECLINE_REASON_HINT}</p>
          </ConfirmDialog>
        )}
      </td>
    </tr>
  );
}

/** An approved idea's content, in the words the contents list uses. */
function ContentState({ idea }: { readonly idea: Idea }) {
  if (idea.content === null) return <span className="muted">{wording.CONTENT_DELETED}</span>;
  const status = idea.content.status;
  return isContentStatus(status) ? <StatusBadge status={status} /> : null;
}

function isContentStatus(value: string): value is ContentStatus {
  return (CONTENT_STATUSES as readonly string[]).includes(value);
}

/** The counts the server would answer, for a row changed before the re-read lands. */
function countsOf(ideas: readonly Idea[]): IdeaBatchCounts {
  const count = (status: Idea['status']): number => ideas.filter((idea) => idea.status === status).length;
  return {
    total: ideas.length,
    drafted: count('drafted'),
    approved: count('approved'),
    declined: count('declined'),
    failed: count('failed'),
    left: count('raw') + count('working'),
  };
}
